import { useTranslation } from 'react-i18next';
import { lockHeightPhrase, lockNextPinPhrase } from '@il-colpo/engine';
import { Icon } from '../../ui/index.ts';
import { useGame } from '../GameContext.tsx';
import { LockObject } from './objects/LockObject.tsx';
import { Bystander, ClueLine, HeardList, useLatestEvent, type PuzzleProps } from './shared.tsx';
import styles from './puzzles.module.css';

interface LockView {
  pins: number;
  progress: number;
  required: number;
  setPins?: number[];
  tactile?: boolean;
  clues: { lockOrder?: number[]; lockHeights?: number[] };
}

const HEIGHTS = [1, 2, 3, 4, 5] as const;
const HEARD = ['lockNextPin', 'lockPinHeight'] as const;

/** Pin lock (GDD §8.1): the locksmith sets pins in the right order at the right height. */
export function LockPanel({ puzzleId, type, v: raw }: PuzzleProps) {
  const { t } = useTranslation('game');
  const { role, act } = useGame();
  const v = raw as unknown as LockView;
  const fell = useLatestEvent(puzzleId, 'lock.fall');
  const set = new Set(v.setPins ?? []);

  if (role === 'locksmith') {
    return (
      <div className={styles['stack']}>
        <LockObject pins={v.pins} set={set} fell={fell ? Number(fell.data['pin']) : null} />
        <p className={styles['progress']}>
          {t('puzzle.lock.progress', { done: v.progress, total: v.required })}
        </p>
        <div className={styles['pins']} role="group" aria-label={t('puzzle.lock.pins')}>
          {Array.from({ length: v.pins }, (_, pin) => (
            <div key={pin} className={[styles['pin'], set.has(pin) ? styles['set'] : ''].join(' ')}>
              <span className={styles['pinNumber']}>
                {set.has(pin) ? <Icon name="lock" size="1em" /> : null} {pin + 1}
              </span>
              {HEIGHTS.map((h) => (
                <button
                  key={h}
                  type="button"
                  className={styles['height']}
                  disabled={set.has(pin)}
                  aria-label={t('puzzle.lock.set', { pin: pin + 1, height: h })}
                  onClick={() => act(puzzleId, { kind: 'lock.set', pin, height: h })}
                >
                  {h}
                </button>
              ))}
            </div>
          ))}
        </div>
        {fell ? (
          <p className={styles['status']} role="status">
            {fell.data['hint'] === 'vibrates'
              ? t('puzzle.lock.vibrates', { pin: Number(fell.data['pin']) + 1 })
              : t('puzzle.lock.falls', { pin: Number(fell.data['pin']) + 1 })}
          </p>
        ) : null}
        <HeardList puzzleId={puzzleId} ids={HEARD} />
      </div>
    );
  }

  const order = v.clues.lockOrder;
  const heights = v.clues.lockHeights;
  if (order) {
    return (
      <div className={styles['stack']}>
        <p className={styles['muted']}>{t('puzzle.lock.orderHint')}</p>
        <ol className={styles['clues']}>
          {order.map((pin, i) => (
            <ClueLine
              key={i}
              phrase={lockNextPinPhrase(pin)}
              highlight={i === v.progress}
              extra={<span className={styles['badge']}>{i + 1}</span>}
            />
          ))}
        </ol>
      </div>
    );
  }
  if (heights) {
    return (
      <div className={styles['stack']}>
        <p className={styles['muted']}>{t('puzzle.lock.heightsHint')}</p>
        <ul className={styles['clues']}>
          {heights.map((h, pin) => (
            <ClueLine key={pin} phrase={lockHeightPhrase(pin, h)} />
          ))}
        </ul>
      </div>
    );
  }
  return <Bystander type={type} />;
}
